/**
 * 画面のエラーの拾い上げと、ダイアログに積んだあとの処理（#456・#459）。
 *
 * @remarks
 * `instrumentation-client.ts`（アプリが動き出す前に 1 回だけ走る）から {@link installErrorReporting} を呼ぶ。
 * - `window` の `error`・`unhandledrejection` を拾ってダイアログに積む（イベントハンドラ・
 *   setTimeout・Promise の中の例外は React のエラー境界に届かないため）
 * - 積んだ 1 件のスタックをソースマップで元の位置に戻し（`lib/errors/source-map.ts`）、
 *   FE サーバーのログへ送る（`lib/errors/client-log.ts`）
 */
import { ApiError } from "@/lib/api/api-error";
import { clientErrorEvent, postClientLog } from "@/lib/errors/client-log";
import { isAbortError, reportUnexpectedError } from "@/lib/errors/from-error";
import { type ErrorReport, onErrorReported, updateErrorReport } from "@/lib/errors/report";
import { resolveStack } from "@/lib/errors/source-map";

/** スタックの復元を待つ上限（過ぎたら元のままログへ送る）。 */
const RESOLVE_TIMEOUT_MS = 4_000;

/** 拾わないエラー（ブラウザの既知の無害な通知・拡張機能・別オリジンのスクリプト）。 */
const IGNORED_MESSAGES = [/^ResizeObserver loop/, /^Script error\.?$/];
const EXTENSION_STACK = /(chrome|moz|safari(-web)?)-extension:\/\//;

let installed = false;

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function afterReported(report: ErrorReport): Promise<void> {
  let current = report;
  if (report.clientStack) {
    const resolved = await withTimeout(
      resolveStack(report.clientStack).catch(() => null),
      RESOLVE_TIMEOUT_MS,
    );
    if (resolved) {
      updateErrorReport(report.id, { resolvedClientStack: resolved });
      current = { ...report, resolvedClientStack: resolved };
    }
  }
  postClientLog(clientErrorEvent(current));
}

/**
 * 拾ったエラーをダイアログに出すべきか（無害な通知・拡張機能・中断・Next の制御用の例外を除く）。
 *
 * @param error - 拾った例外（`event.error`・`event.reason`）。
 * @param message - `ErrorEvent.message`（例外が無いとき）。
 */
export function shouldReportWindowError(error: unknown, message: string | null): boolean {
  if (isAbortError(error)) return false;
  // 401 は customFetch が再ログインへ送っている
  if (error instanceof ApiError && error.status === 401) return false;
  const text = error instanceof Error ? error.message : (message ?? String(error ?? ""));
  if (IGNORED_MESSAGES.some((pattern) => pattern.test(text))) return false;
  if (error instanceof Error && error.stack && EXTENSION_STACK.test(error.stack)) return false;
  const digest =
    typeof error === "object" && error !== null ? (error as { digest?: unknown }).digest : null;
  // redirect()・notFound() などの制御用の例外
  if (typeof digest === "string" && digest.startsWith("NEXT_")) return false;
  return error != null || (message != null && message !== "");
}

/** エラーの拾い上げとダイアログの後処理を有効にする（2 回目以降は何もしない）。 */
export function installErrorReporting(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  onErrorReported((report) => {
    void afterReported(report);
  });
  window.addEventListener("error", (event) => {
    if (!shouldReportWindowError(event.error, event.message)) return;
    reportUnexpectedError(event.error ?? new Error(event.message));
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (!shouldReportWindowError(event.reason, null)) return;
    reportUnexpectedError(event.reason);
  });
}
